import Paper from '@/components/Paper'
import {
    DataTableCustomized,
    createDateFormatter,
    createOptionsFormatter,
    type DataTableColumn,
} from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { getProductsGroup } from '@/modules/ProductGroup/services'
import { ProductGroup } from '@/modules/ProductGroup/types'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const getColumns = (
    t: (key: string) => string
): DataTableColumn<ProductGroup>[] => [
    {
        key: 'product_group_code',
        title: t('product-group-code'),
        width: 130,
        sortable: true,
    },
    {
        key: 'product_group_name',
        title: t('product-group-name'),
        width: 280,
        sortable: true,
    },
    {
        key: 'product_group_description',
        title: t('description'),
        width: 300,
        valueGetter: (row) => row.product_group_description || '-',
    },
    {
        key: 'product_group_hidden',
        title: t('status'),
        width: 100,
        valueGetter: (row) => !row.product_group_hidden,
        formatter: createOptionsFormatter({
            options: [
                { label: t('active'), value: true, color: 'success' },
                { label: t('hidden'), value: false, color: 'warning' },
            ],
            renderMode: 'chip',
            textTransform: 'capitalize',
        }),
    },
    {
        key: 'product_group_created_at',
        title: t('created-at'),
        width: 140,
        sortable: true,
        formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
    },
]

const ProductGroupsTab: React.FC = () => {
    const { t } = useTranslation('settings')

    const { params, queryParams, setParams, searchInputProps } =
        useUrlSearchParams({
            defaults: {
                sort_by: 'product_group_name',
                sort_order: 'asc' as const,
            },
        })

    // Fetch all data, pagination handled client-side by DataTableCustomized
    const apiParams = useMemo(
        () => ({
            q: queryParams.q,
            sort_by: queryParams.sort_by,
            sort_order: queryParams.sort_order,
            page_size: 1000,
        }),
        [queryParams.q, queryParams.sort_by, queryParams.sort_order]
    )

    const {
        data: productGroupsData,
        renderState,
        isFetching,
    } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.PRODUCT_GROUPS, apiParams],
            queryFn: () => getProductsGroup(apiParams),
            placeholderData: true,
        },
        { showLoading: true, showError: true }
    )

    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )

    const rows = productGroupsData?.results || []
    const columns = useMemo(() => getColumns(t), [t])

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <Paper>
            <DataTableCustomized<ProductGroup>
                columns={columns}
                rows={rows}
                rowKey={(row) => row.product_group_id}
                isFetching={isFetching}
                emptyMessage={t('no-data')}
                sorting={{
                    sortBy: params.sort_by,
                    sortOrder: params.sort_order,
                    onSortChange: handleSortChange,
                }}
                pagination={{
                    page: 1,
                    pageSize: 20,
                    total: rows.length,
                }}
                toolbar={{
                    search: {
                        ...searchInputProps,
                        placeholder: t('search-product-group'),
                    },
                }}
                bordered
            />
        </Paper>
    )
}

export default ProductGroupsTab
